import { _js } from '@ifixit/utils/localize';
import { Button } from '@ifixit/ui/components/button';
import { Input } from '@ifixit/ui/components/input';
import { cn } from '@ifixit/ui/lib/utils';
import { useEffect, useId, useRef, useState } from 'react';
import YouTube from 'react-youtube';
import { DualRangeSlider } from 'Shared/editor/RangeSlider';
import type { GuideVideoInfo } from './types';

/**
 * The step's slice of the guide video: the start/end second fields backed
 * by an inline player, so the times can be picked visually — drag the
 * range handles (the player seeks live to the frame under the handle) or
 * play up to the moment and mark it. The number inputs stay as the precise
 * (and screen-reader) path; both write the same field state, and the token
 * rewrite on save is the caller's job.
 *
 * Stopgap scope (#brainstorm with kyle, 2026-07): a per-step editor only.
 * A full timeline showing every step's segment is a later, separate
 * project.
 */

type TimestampFields = { start: string; end: string };

/**
 * Compact only in padding — the height stays button-md so these line up with
 * the h-9 number inputs they share an items-center row with.
 */
const smallButton = 'button-md button-outline px-3';

/** One decimal, matching the token precision the number inputs use. */
function formatSeconds(value: number): string {
   return String(Math.round(value * 10) / 10);
}

function parseField(text: string): number | null {
   const parsed = Number.parseFloat(text.trim());
   return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

export function StepTimestampEditor({
   video,
   fields,
   invalid,
   onChange,
}: {
   video: GuideVideoInfo;
   fields: TimestampFields;
   /** Live validity from the caller (half-filled pair, start >= end). */
   invalid: boolean;
   onChange: (fields: TimestampFields) => void;
}) {
   // Stable per-instance ids for the bare start/end fields, which have
   // aria-labels but would otherwise render with no id at all.
   const fieldBaseId = useId();
   const youTubeRef = useRef<YouTube | null>(null);
   const videoRef = useRef<HTMLVideoElement | null>(null);
   // Set once the player has metadata; the slider needs it for its scale.
   const [duration, setDuration] = useState<number | null>(null);
   const [previewing, setPreviewing] = useState(false);

   const seekTo = (seconds: number) => {
      if (video.type === 'youtube') {
         // internalPlayer is the youtube-player promise proxy; a seek
         // before ready just resolves once the player is up.
         void youTubeRef.current?.internalPlayer?.seekTo(seconds, true);
      } else if (videoRef.current) {
         videoRef.current.currentTime = seconds;
      }
   };

   const getCurrentTime = async (): Promise<number | null> => {
      if (video.type === 'youtube') {
         const time = await youTubeRef.current?.internalPlayer?.getCurrentTime();
         return typeof time === 'number' ? time : null;
      }
      return videoRef.current?.currentTime ?? null;
   };

   const play = () => {
      if (video.type === 'youtube') {
         void youTubeRef.current?.internalPlayer?.playVideo();
      } else {
         void videoRef.current?.play();
      }
   };

   const pause = () => {
      if (video.type === 'youtube') {
         void youTubeRef.current?.internalPlayer?.pauseVideo();
      } else {
         videoRef.current?.pause();
      }
   };

   // Seeks from slider drags, trailing-throttled: a drag emits a change per
   // pixel, and flooding the player with seeks makes YouTube stutter.
   const pendingSeek = useRef<number | null>(null);
   const seekTimer = useRef<number | null>(null);
   const requestSeek = (seconds: number) => {
      pendingSeek.current = seconds;
      if (seekTimer.current !== null) {
         return;
      }
      seekTimer.current = window.setTimeout(() => {
         seekTimer.current = null;
         if (pendingSeek.current !== null) {
            seekTo(pendingSeek.current);
            pendingSeek.current = null;
         }
      }, 150);
   };
   useEffect(
      () => () => {
         if (seekTimer.current !== null) {
            window.clearTimeout(seekTimer.current);
         }
      },
      []
   );

   // Preview playback: watch the playhead and stop at the segment's end.
   // Polled (like the guide view player) because neither player surfaces a
   // "reached time X" event.
   const endSeconds = parseField(fields.end);
   useEffect(() => {
      if (!previewing || endSeconds === null) {
         return;
      }
      const intervalId = window.setInterval(() => {
         void getCurrentTime().then(time => {
            if (time !== null && time >= endSeconds) {
               pause();
               setPreviewing(false);
            }
         });
      }, 250);
      return () => window.clearInterval(intervalId);
      // getCurrentTime/pause are stable in behavior (refs); listing them
      // would need useCallback noise for no correctness gain.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [previewing, endSeconds]);

   const startSeconds = parseField(fields.start);
   const hasFields = fields.start.trim() !== '' || fields.end.trim() !== '';

   // Slider positions while the fields are mid-edit: an unparsable start
   // reads as 0, an unparsable end as the video's end, so one bad keystroke
   // wobbles a handle instead of unmounting the slider.
   const sliderMax = duration ?? 0;
   const sliderStart = Math.min(startSeconds ?? 0, sliderMax);
   const sliderEnd = Math.min(endSeconds ?? sliderMax, sliderMax);

   const changeRange = (start: number, end: number) => {
      // Seek to the handle that moved so the paused frame previews it.
      if (start !== sliderStart) {
         requestSeek(start);
      } else if (end !== sliderEnd) {
         requestSeek(end);
      }
      setPreviewing(false);
      onChange({ start: formatSeconds(start), end: formatSeconds(end) });
   };

   const markStart = () => {
      void getCurrentTime().then(time => {
         if (time !== null) {
            onChange({ ...fields, start: formatSeconds(time) });
         }
      });
   };

   const markEnd = () => {
      void getCurrentTime().then(time => {
         if (time !== null) {
            onChange({ ...fields, end: formatSeconds(time) });
         }
      });
   };

   const preview = () => {
      if (previewing) {
         pause();
         setPreviewing(false);
         return;
      }
      if (startSeconds === null || endSeconds === null || startSeconds >= endSeconds) {
         return;
      }
      seekTo(startSeconds);
      play();
      setPreviewing(true);
   };

   return (
      <div>
         <span className="mb-1 block text-sm font-medium text-gray-950">
            {_js('Video timestamps (seconds)')}
         </span>

         <div className="max-w-2xl">
            <div className="aspect-video w-full overflow-hidden rounded-md bg-black">
               {video.type === 'youtube' ? (
                  <YouTube
                     videoId={video.videoId}
                     ref={youTubeRef}
                     className="size-full"
                     iframeClassName="size-full"
                     opts={{ width: '100%', height: '100%', playerVars: { rel: 0 } }}
                     onReady={event => {
                        void Promise.resolve(event.target.getDuration()).then((seconds: number) => {
                           if (seconds > 0) {
                              setDuration(seconds);
                           }
                        });
                     }}
                  />
               ) : (
                  <video
                     ref={videoRef}
                     controls
                     preload="metadata"
                     className="size-full"
                     onLoadedMetadata={event => {
                        const seconds = event.currentTarget.duration;
                        if (Number.isFinite(seconds) && seconds > 0) {
                           setDuration(seconds);
                        }
                     }}
                  >
                     {video.videoUrls?.mp4_720 && (
                        <source src={video.videoUrls.mp4_720} type="video/mp4" />
                     )}
                     {video.videoUrls?.mp4_592 && (
                        <source src={video.videoUrls.mp4_592} type="video/mp4" />
                     )}
                     {video.videoUrls?.webm && (
                        <source src={video.videoUrls.webm} type="video/webm" />
                     )}
                     {video.videoUrls?.ogg && <source src={video.videoUrls.ogg} type="video/ogg" />}
                  </video>
               )}
            </div>

            {duration !== null &&
               (hasFields ? (
                  <div className="mt-1 px-1">
                     <DualRangeSlider
                        max={sliderMax}
                        step={0.1}
                        low={sliderStart}
                        high={sliderEnd}
                        lowLabel={_js('Step start time')}
                        highLabel={_js('Step end time')}
                        onValueChange={changeRange}
                     />
                  </div>
               ) : (
                  <Button
                     type="button"
                     className={cn(smallButton, 'mt-2')}
                     onClick={() => onChange({ start: '0', end: formatSeconds(duration) })}
                  >
                     {_js('Add timestamps')}
                  </Button>
               ))}
         </div>

         {hasFields && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
               <Button type="button" className={smallButton} onClick={markStart}>
                  {_js('Mark start')}
               </Button>
               <Input
                  id={`${fieldBaseId}-start`}
                  className="w-24"
                  type="number"
                  min={0}
                  step={0.1}
                  inputMode="decimal"
                  aria-label={_js('Start time in seconds')}
                  value={fields.start}
                  onChange={event => onChange({ ...fields, start: event.target.value })}
               />
               <span className="text-gray-500">{_js('to')}</span>
               <Input
                  id={`${fieldBaseId}-end`}
                  className="w-24"
                  type="number"
                  min={0}
                  step={0.1}
                  inputMode="decimal"
                  aria-label={_js('End time in seconds')}
                  value={fields.end}
                  onChange={event => onChange({ ...fields, end: event.target.value })}
               />
               <Button type="button" className={smallButton} onClick={markEnd}>
                  {_js('Mark end')}
               </Button>
               <Button
                  type="button"
                  className={smallButton}
                  disabled={invalid || startSeconds === null || endSeconds === null}
                  onClick={preview}
               >
                  {previewing ? _js('Stop') : _js('Preview')}
               </Button>
            </div>
         )}

         {invalid ? (
            <p className="mt-1 text-xs text-red-600">
               {_js('Timestamps need both a start and an end, with the start before the end.')}
            </p>
         ) : (
            <p className="mt-1 text-xs text-gray-500">
               {hasFields
                  ? _js(
                       'Drag the handles or play the video and mark the times. Clear both fields for no timestamp.'
                    )
                  : _js('The part of the video this step covers.')}
            </p>
         )}
      </div>
   );
}
